import { type ParentComponent, Show, createMemo, onMount } from "solid-js";
import { ArtifactPanel } from "~/components/chat/ArtifactPanel";
import { ResizableLayout } from "~/components/chat/ResizableLayout";
import { BreakpointBug } from "~/components/debug/BreakpointBug";
import { useArtifactStore } from "~/stores/ArtifactStore";
import { AppUIContextProvider, NavigationPaneMode, isExtendedPaneVisible, useAppUI } from "./AppUIContext";
import { ExtendedPane } from "./ExtendedPane";
import { NavigationPane } from "./NavigationPane";
import { useLocation } from "@solidjs/router";
import { StripeReturnModal, useStripeURLReturnState } from "~/components/billing/StripeReturnModal";

export const MinimalLayout: ParentComponent = (props) => {
  const { state, actions } = useAppUI();
  const [artifactStore, artifactActions] = useArtifactStore();

  const location = useLocation();
  const showNavigationPane = () => !location.pathname.startsWith("/auth");
  // Calculate layout dimensions - only apply margin when docked
  const navigationWidth = createMemo(() => {
    if (!showNavigationPane()) return 0;
    return state.navigationPane.mode === NavigationPaneMode.DOCKED ? state.navigationPane.width : 40;
  });

  // Calculate main content area positioning for navigation pane and extended pane
  const contentStyles = createMemo(() => {
    const navWidth = navigationWidth();
    const extendedPaneWidth = isExtendedPaneVisible(state.extendedPane) ? state.extendedPane.width : 0;

    let marginLeft = 0;
    let marginRight = 0;

    // Navigation pane always takes up left space when expanded
    if (navWidth > 0) {
      marginLeft = navWidth;
    }

    // Extended pane takes up right space when visible
    if (extendedPaneWidth > 0) {
      marginRight = extendedPaneWidth;
    }

    return {
      "margin-left": `${marginLeft}px`,
      "margin-right": `${marginRight}px`,
    };
  });

  // Add keyboard shortcut handler for debug mode
  onMount(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for CMD + SHIFT + CTRL + P
      if (e.metaKey && e.shiftKey && e.ctrlKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        actions.toggleDebugMode();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  return (
    <div class="min-h-screen relative" data-testid="minimal-layout">
      {/* Navigation Pane */}
      <Show when={showNavigationPane()}>
        <NavigationPane />
      </Show>

      {/* Main Content Area with ResizableLayout for artifact panel */}
      <div class="min-h-screen" style={contentStyles()} data-testid="main-content-area">
        <ResizableLayout
          class="h-screen"
          leftContent={props.children}
          rightContent={
            <Show when={artifactStore().isPanelVisible && !isExtendedPaneVisible(state.extendedPane)}>
              <ArtifactPanel
                artifacts={artifactStore().artifacts}
                activeArtifactId={artifactStore().activeArtifactId}
                onArtifactSelect={artifactActions.setActiveArtifact}
                onArtifactClose={artifactActions.removeArtifact}
              />
            </Show>
          }
          showRightPanel={artifactStore().isPanelVisible && !isExtendedPaneVisible(state.extendedPane)}
          defaultRightWidth={artifactStore().panelWidth}
          minLeftWidth={480}
          minRightWidth={320}
          onResize={(_leftWidth, rightWidth) => {
            artifactActions.setPanelWidth(rightWidth);
          }}
        />
      </div>

      {/* Extended Pane */}
      <ExtendedPane />

      {/* Debug Tools */}
      <Show when={state.isDebugMode}>
        <BreakpointBug />
      </Show>

      <StripeReturnModal />
    </div>
  );
};

// Export the provider wrapper for convenience
export const MinimalLayoutProvider: ParentComponent = (props) => {
  return (
    <AppUIContextProvider>
      <MinimalLayout>{props.children}</MinimalLayout>
    </AppUIContextProvider>
  );
};

// Re-export the context provider for direct use
export { AppUIContextProvider } from "./AppUIContext";
